import { useEffect, useState } from 'react';
import type { LogbookTransition } from '../types/logbook';
import { getLogbookTransitions } from '../lib/api/endpoints';
import { logbookTransitionsFromDto } from '../lib/api/mappers';

/**
 * FE-13 — los cambios de UNA novedad: quién la registró, quién la corrigió, y
 * el "de qué a qué" de cada campo.
 *
 * Se pide bajo demanda (al abrir el detalle), no junto al listado: son N+1
 * peticiones si se pidiera para cada fila del timeline, y la inmensa mayoría de
 * las novedades nunca se corrigen — sus cambios son una sola línea.
 *
 * `entryId` en `null` limpia el estado en vez de dejar colgados los cambios de
 * la novedad anterior: al cerrar el detalle y abrir otro, lo que se pinta
 * mientras carga tiene que ser vacío, no la corrección de otra persona.
 */
export function useLogbookTransitions(entryId: string | null) {
  const [transitions, setTransitions] = useState<LogbookTransition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!entryId) {
      setTransitions([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setTransitions([]);
      try {
        const res = await getLogbookTransitions(entryId);
        if (cancelled) return;
        setTransitions(logbookTransitionsFromDto(res.items));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setTransitions([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando los cambios de la novedad'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [entryId]);

  return { transitions, isLoading, error };
}
